import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ClipboardList, Calendar, Search, Loader2, Plus, Filter, Eye,
  CheckCircle2, Clock, AlertTriangle, FileText, Image as ImageIcon,
  Star, MessageSquare, Download, Pen
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format, isPast } from "date-fns";
import { ar } from "date-fns/locale";
import AddAssignmentDialog from "@/components/dialogs/AddAssignmentDialog";
import { getSignedFileUrl } from "@/lib/storage-utils";
import { parseFileUrls } from "@/lib/file-utils";
import ImageAnnotator from "@/components/ImageAnnotator";

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  pending: { label: "لم يُسلّم", color: "bg-muted text-muted-foreground", icon: Clock },
  submitted: { label: "مُسلّم", color: "bg-blue-100 text-blue-700", icon: FileText },
  graded: { label: "مُصحّح", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
};

const Assignments = () => {
  const { profile, user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const nurseryId = profile?.nursery_id;
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [selectedAssignment, setSelectedAssignment] = useState<string | null>(null);
  const [gradingSubmission, setGradingSubmission] = useState<any | null>(null);
  const [gradeScore, setGradeScore] = useState("");
  const [gradeNotes, setGradeNotes] = useState("");
  const [submissionFileUrl, setSubmissionFileUrl] = useState<string | null>(null);
  const [assignmentFileUrl, setAssignmentFileUrl] = useState<string | null>(null);
  const [showAnnotator, setShowAnnotator] = useState(false);
  const [savingAnnotation, setSavingAnnotation] = useState(false);

  const { data: classes } = useQuery({
    queryKey: ["classes-list", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: assignments, isLoading, refetch } = useQuery({
    queryKey: ["assignments", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assignments")
        .select("*, classes(name), lessons(title)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: submissions, refetch: refetchSubs } = useQuery({
    queryKey: ["assignment-submissions", selectedAssignment],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("assignment_submissions")
        .select("*, children(full_name, photo_url)")
        .eq("assignment_id", selectedAssignment!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedAssignment,
  });

  // Get children for the selected assignment's class
  const selectedAssignmentData = (assignments || []).find((a: any) => a.id === selectedAssignment);

  const { data: classChildren } = useQuery({
    queryKey: ["class-children", selectedAssignmentData?.class_id],
    queryFn: async () => {
      const { data } = await supabase
        .from("children")
        .select("id, full_name, photo_url")
        .eq("class_id", selectedAssignmentData!.class_id!)
        .eq("nursery_id", nurseryId!)
        .order("full_name");
      return data || [];
    },
    enabled: !!selectedAssignmentData?.class_id && !!nurseryId,
  });

  const filteredAssignments = (assignments || []).filter((a: any) => {
    const matchSearch = a.title.includes(search) || (a.description && a.description.includes(search));
    const matchClass = classFilter === "all" || a.class_id === classFilter;
    return matchSearch && matchClass;
  });

  const handleGrade = async () => {
    if (!gradingSubmission || !gradeScore) return;
    const { error } = await supabase
      .from("assignment_submissions")
      .update({
        score: parseFloat(gradeScore),
        teacher_notes: gradeNotes || null,
        status: "graded",
        graded_at: new Date().toISOString(),
        graded_by: user?.id,
      })
      .eq("id", gradingSubmission.id);

    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "✅ تم التصحيح بنجاح" });
      setGradingSubmission(null);
      setGradeScore("");
      setGradeNotes("");
      refetchSubs();
    }
  };

  const handleSaveAnnotation = async (dataUrl: string) => {
    if (!gradingSubmission || !nurseryId) return;
    setSavingAnnotation(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const path = `${nurseryId}/annotations/${gradingSubmission.id}_${Date.now()}.jpg`;
      const { error: uploadErr } = await supabase.storage.from("homework-files").upload(path, blob, { contentType: "image/jpeg" });
      if (uploadErr) throw uploadErr;

      // Save annotated file path in teacher_notes or a reference
      const currentNotes = gradeNotes ? gradeNotes + "\n" : "";
      setGradeNotes(currentNotes + "📝 تم إرفاق صورة مُصحّحة");

      // Update submission with annotated file reference
      await supabase
        .from("assignment_submissions")
        .update({ file_url: path })
        .eq("id", gradingSubmission.id);

      toast({ title: "✅ تم حفظ الصورة المُصحّحة" });
      setShowAnnotator(false);
    } catch (err: any) {
      toast({ title: "خطأ في حفظ الصورة", description: err.message, variant: "destructive" });
    }
    setSavingAnnotation(false);
  };

  const openSubmissionFile = async (url: string) => {
    const urls = parseFileUrls(url);
    for (const u of urls) {
      const signed = await getSignedFileUrl("homework-files", u);
      if (signed) window.open(signed, "_blank");
    }
  };

  const openGrading = async (sub: any) => {
    setGradingSubmission(sub);
    setGradeScore(sub.score?.toString() || "");
    setGradeNotes(sub.teacher_notes || "");
    const urls = parseFileUrls(sub.file_url);
    if (urls.length > 0) {
      const signed = await getSignedFileUrl("homework-files", urls[0]);
      setSubmissionFileUrl(signed);
    } else {
      setSubmissionFileUrl(null);
    }
  };

  const openAssignmentDetail = async (a: any) => {
    setSelectedAssignment(a.id);
    const urls = parseFileUrls(a.file_url);
    if (urls.length > 0) {
      const signed = await getSignedFileUrl("homework-files", urls[0]);
      setAssignmentFileUrl(signed);
    } else {
      setAssignmentFileUrl(null);
    }
  };

  const submittedIds = new Set((submissions || []).map((s: any) => s.child_id));
  const pendingChildren = (classChildren || []).filter((c: any) => !submittedIds.has(c.id));

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    try { return format(new Date(d), "d MMM yyyy", { locale: ar }); } catch { return d; }
  };

  const submissionCounts = (assignments || []).reduce((acc: Record<string, number>, a: any) => {
    // We'll count via a separate query; for now use 0
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-primary" />
            الواجبات المدرسية
          </h1>
          <p className="text-muted-foreground text-sm mt-1">إنشاء الواجبات ومتابعة التسليم والتصحيح</p>
        </div>
        <AddAssignmentDialog
          classes={classes || []}
          nurseryId={nurseryId!}
          onSuccess={refetch}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="w-4 h-4 ml-2 text-muted-foreground" />
            <SelectValue placeholder="الفصل" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الفصول</SelectItem>
            {(classes || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assignments List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">الواجبات</h2>
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filteredAssignments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>لا توجد واجبات</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[600px]">
              <div className="space-y-2">
                {filteredAssignments.map((a: any, i: number) => {
                  const overdue = isPast(new Date(a.due_date));
                  return (
                    <motion.div
                      key={a.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Card
                        className={`shadow-card cursor-pointer transition-all hover:shadow-elevated ${
                          selectedAssignment === a.id ? "ring-2 ring-primary" : ""
                        }`}
                        onClick={() => openAssignmentDetail(a)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-foreground truncate">{a.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {(a as any).classes?.name || "بدون فصل"}
                                {(a as any).lessons?.title && ` • ${(a as any).lessons.title}`}
                              </p>
                            </div>
                            {a.file_url && (
                              <Badge variant="outline" className="text-[10px]">
                                <FileText className="w-3 h-3 ml-0.5" />
                                {parseFileUrls(a.file_url).length}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              {formatDate(a.due_date)}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                <Star className="w-3 h-3 ml-1" />
                                {a.max_score}
                              </Badge>
                              {overdue && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="w-3 h-3 ml-1" />
                                  منتهي
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* Submissions Panel */}
        <div className="lg:col-span-2">
          {!selectedAssignment ? (
            <Card className="shadow-card">
              <CardContent className="py-16 text-center">
                <Eye className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">اختر واجبًا لعرض التسليمات</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Assignment Info */}
              {selectedAssignmentData && (
                <Card className="shadow-card">
                  <CardContent className="p-4">
                    <h3 className="font-bold text-foreground text-lg">{selectedAssignmentData.title}</h3>
                    {selectedAssignmentData.description && (
                      <p className="text-sm text-muted-foreground mt-1">{selectedAssignmentData.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span>📅 الموعد: {formatDate(selectedAssignmentData.due_date)}</span>
                      <span>⭐ الدرجة: {selectedAssignmentData.max_score}</span>
                    </div>
                     {selectedAssignmentData.file_url && (
                       <div className="mt-3 flex flex-wrap gap-2">
                         {parseFileUrls(selectedAssignmentData.file_url).map((url: string, idx: number) => (
                           <Button key={idx} variant="outline" size="sm" onClick={async () => {
                             const signed = await getSignedFileUrl("homework-files", url);
                             if (signed) window.open(signed, "_blank");
                           }}>
                             <Download className="w-4 h-4 ml-1" />
                             ملف {idx + 1}
                           </Button>
                         ))}
                       </div>
                     )}
                  </CardContent>
                </Card>
              )}

              <Tabs defaultValue="submitted" dir="rtl">
                <TabsList>
                  <TabsTrigger value="submitted">
                    التسليمات ({(submissions || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="pending">
                    لم يسلّموا ({pendingChildren.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="submitted" className="mt-3 space-y-2">
                  {(submissions || []).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      لا توجد تسليمات بعد
                    </div>
                  ) : (
                    (submissions || []).map((sub: any) => {
                      const st = statusConfig[sub.status] || statusConfig.pending;
                      const StIcon = st.icon;
                      return (
                        <Card key={sub.id} className="shadow-card">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarImage src={sub.children?.photo_url || ""} />
                                <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                                  {sub.children?.full_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm">{sub.children?.full_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {sub.submitted_at ? formatDate(sub.submitted_at) : "-"}
                                </p>
                              </div>
                              <Badge className={`text-xs ${st.color}`}>
                                <StIcon className="w-3 h-3 ml-1" />
                                {st.label}
                              </Badge>
                              {sub.status === "graded" && (
                                <Badge variant="outline" className="text-xs font-bold">
                                  {sub.score}/{selectedAssignmentData?.max_score}
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-2 mt-3">
                              {sub.file_url && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openSubmissionFile(sub.file_url)}
                                >
                                  <ImageIcon className="w-4 h-4 ml-1" />
                                  عرض الملفات ({parseFileUrls(sub.file_url).length})
                                </Button>
                              )}
                              {sub.text_answer && (
                                <Badge variant="secondary" className="text-xs">إجابة نصية</Badge>
                              )}
                              <Button
                                variant="default"
                                size="sm"
                                className="mr-auto"
                                onClick={() => openGrading(sub)}
                              >
                                <CheckCircle2 className="w-4 h-4 ml-1" />
                                {sub.status === "graded" ? "تعديل التصحيح" : "تصحيح"}
                              </Button>
                            </div>

                            {sub.teacher_notes && (
                              <div className="mt-2 p-2 bg-muted rounded text-xs text-muted-foreground flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                {sub.teacher_notes}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </TabsContent>

                <TabsContent value="pending" className="mt-3 space-y-2">
                  {pendingChildren.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      جميع الأطفال سلّموا الواجب ✅
                    </div>
                  ) : (
                    pendingChildren.map((child: any) => (
                      <Card key={child.id} className="shadow-card">
                        <CardContent className="p-3 flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={child.photo_url || ""} />
                            <AvatarFallback className="bg-muted text-muted-foreground text-xs">
                              {child.full_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <p className="font-medium text-foreground text-sm">{child.full_name}</p>
                          <Badge variant="outline" className="text-xs mr-auto">
                            <Clock className="w-3 h-3 ml-1" />
                            في الانتظار
                          </Badge>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      {/* Grading Dialog */}
      <Dialog open={!!gradingSubmission && !showAnnotator} onOpenChange={(open) => !open && setGradingSubmission(null)}>
        <DialogContent className="max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle>تصحيح الواجب — {gradingSubmission?.children?.full_name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Submitted file preview */}
            {submissionFileUrl && (
              <div className="rounded-lg overflow-hidden border">
                <img
                  src={submissionFileUrl}
                  alt="صورة الحل"
                  className="w-full max-h-[300px] object-contain bg-muted"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
                <div className="p-2 flex items-center justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => window.open(submissionFileUrl, "_blank")}>
                    <Download className="w-4 h-4 ml-1" />
                    فتح بحجم كامل
                  </Button>
                  <Button variant="default" size="sm" onClick={() => setShowAnnotator(true)}>
                    <Pen className="w-4 h-4 ml-1" />
                    رسم وتعليق على الصورة
                  </Button>
                </div>
              </div>
            )}

            {gradingSubmission?.text_answer && (
              <div className="p-3 bg-muted rounded-lg">
                <Label className="text-xs text-muted-foreground mb-1">الإجابة النصية:</Label>
                <p className="text-sm text-foreground">{gradingSubmission.text_answer}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>الدرجة (من {selectedAssignmentData?.max_score})</Label>
              <Input
                type="number"
                min={0}
                max={selectedAssignmentData?.max_score || 100}
                value={gradeScore}
                onChange={(e) => setGradeScore(e.target.value)}
                placeholder="أدخل الدرجة"
              />
            </div>

            <div className="space-y-2">
              <Label>ملاحظات المعلم</Label>
              <Textarea
                value={gradeNotes}
                onChange={(e) => setGradeNotes(e.target.value)}
                placeholder="أكتب ملاحظاتك هنا..."
                rows={3}
              />
            </div>

            <Button className="w-full" onClick={handleGrade} disabled={!gradeScore}>
              <CheckCircle2 className="w-4 h-4 ml-2" />
              حفظ التصحيح
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Annotation Dialog */}
      <Dialog open={showAnnotator} onOpenChange={(open) => !open && setShowAnnotator(false)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="w-5 h-5 text-primary" />
              تصحيح الصورة — {gradingSubmission?.children?.full_name}
            </DialogTitle>
          </DialogHeader>
          {submissionFileUrl && (
            <ImageAnnotator
              imageUrl={submissionFileUrl}
              onSave={handleSaveAnnotation}
              saving={savingAnnotation}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Assignments;
